import React, { useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'

// ---------------------------------------------------------------------------
// Priority helpers
// ---------------------------------------------------------------------------

const PRIORITY_CONFIG = {
  CRITICAL: { icon: '🔴', label: 'Critical', bg: '#fef2f2', color: '#b91c1c', border: '#fca5a5', chipBg: '#fee2e2' },
  HIGH:     { icon: '🟠', label: 'High',     bg: '#fff7ed', color: '#c2410c', border: '#fdba74', chipBg: '#ffedd5' },
  MEDIUM:   { icon: '🟡', label: 'Medium',   bg: '#fefce8', color: '#854d0e', border: '#fde047', chipBg: '#fef9c3' },
}

function parsePriority(str) {
  const m = typeof str === 'string' && str.match(/^\[(CRITICAL|HIGH|MEDIUM)\]\s*/)
  if (!m) return { level: null, text: str ?? '' }
  return { level: m[1], text: str.slice(m[0].length) }
}

// ---------------------------------------------------------------------------
// JSON parsing — multi-strategy, hardened against LLM formatting errors
// ---------------------------------------------------------------------------

/** Strip markdown code fences from LLM output */
function stripFences(s) {
  return s.trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```\s*$/, '')
}

/**
 * Fix literal (unescaped) newlines and carriage returns that appear inside
 * JSON string values — the most common LLM formatting error.
 * Walks char-by-char tracking in-string state so we only touch string contents.
 */
function fixLiteralNewlines(str) {
  let result = ''
  let inString = false
  let escaped = false
  for (let i = 0; i < str.length; i++) {
    const ch = str[i]
    if (escaped) {
      result += ch
      escaped = false
      continue
    }
    if (ch === '\\' && inString) {
      result += ch
      escaped = true
      continue
    }
    if (ch === '"') {
      inString = !inString
      result += ch
      continue
    }
    if (inString) {
      if (ch === '\n') { result += '\\n'; continue }
      if (ch === '\r') { result += '\\r'; continue }
      if (ch === '\t') { result += '\\t'; continue }
    }
    result += ch
  }
  return result
}

/**
 * Strategy 3: positional extraction — robust against unescaped quotes inside string values.
 * Uses indexOf/lastIndexOf anchored to known structural keys instead of regex captures.
 */
function extractKeysFromRaw(raw) {
  const result = {}

  const findingsKeyIdx = raw.indexOf('"findings"')
  const followUpKeyIdx = raw.indexOf('"follow_up_items"')

  // report_md: opening " after "report_md": → last " before "findings"
  const reportMdKeyIdx = raw.indexOf('"report_md"')
  if (reportMdKeyIdx !== -1 && findingsKeyIdx !== -1) {
    const colonIdx = raw.indexOf(':', reportMdKeyIdx)
    const openQuoteIdx = raw.indexOf('"', colonIdx + 1)
    const closeQuoteIdx = raw.lastIndexOf('"', findingsKeyIdx - 1)
    if (openQuoteIdx !== -1 && closeQuoteIdx > openQuoteIdx) {
      let rawMd = raw.slice(openQuoteIdx + 1, closeQuoteIdx)
      rawMd = rawMd.replace(/\\n/g, '\n').replace(/\\r/g, '').replace(/\\t/g, '\t').replace(/\\"/g, '"').replace(/\\\\/g, '\\')
      result.report_md = rawMd
    }
  }

  // findings: [ after "findings": → last ] before "follow_up_items"
  if (findingsKeyIdx !== -1 && followUpKeyIdx !== -1) {
    const colonIdx = raw.indexOf(':', findingsKeyIdx)
    const openBracket = raw.indexOf('[', colonIdx)
    const closeBracket = raw.lastIndexOf(']', followUpKeyIdx)
    if (openBracket !== -1 && closeBracket > openBracket) {
      const arr = raw.slice(openBracket, closeBracket + 1)
      try { result.findings = JSON.parse(arr) } catch {
        try { result.findings = JSON.parse(fixLiteralNewlines(arr)) } catch { /* skip */ }
      }
    }
  }

  // follow_up_items: [ after "follow_up_items": → last ] in string
  if (followUpKeyIdx !== -1) {
    const colonIdx = raw.indexOf(':', followUpKeyIdx)
    const openBracket = raw.indexOf('[', colonIdx)
    const closeBracket = raw.lastIndexOf(']')
    if (openBracket !== -1 && closeBracket > openBracket) {
      const arr = raw.slice(openBracket, closeBracket + 1)
      try { result.follow_up_items = JSON.parse(arr) } catch {
        try { result.follow_up_items = JSON.parse(fixLiteralNewlines(arr)) } catch { /* skip */ }
      }
    }
  }

  return Object.keys(result).length > 0 ? result : null
}

/**
 * Main parser — tries three strategies in order.
 * Returns a parsed object or null if everything fails.
 */
function tryParseAgentJson(s) {
  if (typeof s !== 'string') return null
  const t = stripFences(s)

  // S1: direct parse
  const m1 = t.match(/\{[\s\S]*\}/)
  if (m1) { try { return JSON.parse(m1[0]) } catch { /* fall through */ } }

  // S2: fix literal newlines then parse
  const fixed = fixLiteralNewlines(t)
  const m2 = fixed.match(/\{[\s\S]*\}/)
  if (m2) { try { return JSON.parse(m2[0]) } catch { /* fall through */ } }

  // S3: key-by-key regex extraction
  return extractKeysFromRaw(t)
}

// ---------------------------------------------------------------------------
// providerNote parser
// "Dry run — email not sent · watermarks unchanged · 3 link(s) processed · analyzer: anthropic / claude-sonnet-4-6"
// ---------------------------------------------------------------------------

function parseProviderNote(s) {
  if (!s || typeof s !== 'string') return null
  const str = s.trim()
  const isStagePreview = /stage preview/i.test(str)
  const isDryRun = /dry run/i.test(str)
  const isLive = /live run/i.test(str)
  const emailNotSent = /email not sent/i.test(str)
  const linksMatch = str.match(/(\d+)\s+link/i)
  const links = linksMatch ? parseInt(linksMatch[1], 10) : null
  const modelMatch = str.match(/(?:analyzer|task)[:\s]+([^\n·•]+)/i)
  const model = modelMatch ? modelMatch[1].trim() : null
  return { isStagePreview, isDryRun, isLive, emailNotSent, links, model, raw: str }
}

// ---------------------------------------------------------------------------
// Shared small components
// ---------------------------------------------------------------------------

function SectionHeader({ label, count, right, action }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
      padding: '10px 16px', borderRadius: '8px 8px 0 0',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {label}
        </span>
        {count != null && (
          <span style={{ fontSize: 10, fontWeight: 700, background: '#e2e8f0', color: '#64748b', borderRadius: 10, padding: '1px 7px' }}>
            {count}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {right && <span style={{ fontSize: 11, color: '#94a3b8' }}>{right}</span>}
        {action}
      </div>
    </div>
  )
}

function PriorityChip({ level }) {
  if (!level) return null
  const cfg = PRIORITY_CONFIG[level]
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
      background: cfg.chipBg, color: cfg.color, flexShrink: 0, whiteSpace: 'nowrap',
    }}>
      {cfg.icon} {cfg.label}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Email iframe helper
// ---------------------------------------------------------------------------

function wrapEmailSrcDoc(htmlFragment) {
  const safe = typeof htmlFragment === 'string' ? htmlFragment : ''
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<style>
  * { box-sizing: border-box; }
  body {
    margin: 0;
    padding: 24px 16px;
    background: #f1f5f9;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: 14px;
    line-height: 1.7;
    color: #1e293b;
  }
  .email-wrap {
    max-width: 680px;
    margin: 0 auto;
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 2px 8px rgba(15,23,42,0.07);
    overflow: hidden;
  }
  .email-body {
    padding: 28px 32px;
  }
  h1 { font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 16px; line-height: 1.3; }
  h2 { font-size: 16px; font-weight: 700; color: #0f172a; margin: 28px 0 10px; padding-bottom: 6px; border-bottom: 1px solid #e2e8f0; }
  h3 { font-size: 14px; font-weight: 700; color: #334155; margin: 18px 0 6px; }
  p  { margin: 0 0 12px; }
  p:last-child { margin-bottom: 0; }
  ul, ol { padding-left: 22px; margin: 6px 0 12px; }
  li { margin-bottom: 8px; padding-left: 4px; }
  li:last-child { margin-bottom: 0; }
  strong { color: #0f172a; font-weight: 600; }
  em { color: #475569; font-style: italic; }
  a  { color: #2563eb; text-decoration: underline; }
  hr { border: none; border-top: 1px solid #e2e8f0; margin: 20px 0; }
  table { width: 100%; border-collapse: collapse; margin: 12px 0; }
  th { background: #f8fafc; padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 700; color: #475569; border-bottom: 2px solid #e2e8f0; }
  td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; font-size: 13px; vertical-align: top; }
  code { font-size: 12px; background: #f1f5f9; padding: 1px 5px; border-radius: 4px; font-family: 'SF Mono', 'Fira Code', monospace; color: #0f172a; }
  blockquote { border-left: 3px solid #cbd5e1; margin: 12px 0; padding: 8px 16px; background: #f8fafc; color: #475569; border-radius: 0 4px 4px 0; }
</style>
</head>
<body>
  <div class="email-wrap">
    <div class="email-body">
      ${safe}
    </div>
  </div>
</body>
</html>`
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/** Human-readable analyzer + task creator outputs (preview modal and full-run panel). */
export function BasecampAgentPipelineOutput({
  mode = 'full',
  analyzerText,
  taskCreatorText,
  providerNote,
  analyzerInputExcerpt,
  // Server pre-parsed fields (preferred; client-side parsing is the fallback)
  analyzerReportMd: preParsedReportMd,
  analyzerFindings: preParsedFindings,
  analyzerFollowUpItems: preParsedFollowUps,
  taskSubject: preParsedSubject,
  taskHtmlBody: preParsedHtmlBody,
}) {
  const showAnalyzer = mode === 'full' || mode === 'analyzer'
  const showTask = mode === 'full' || mode === 'task_creator'
  const [briefingExpanded, setBriefingExpanded] = useState(false)
  const iframeRef = useRef(null)

  const parsedAnalyzer = tryParseAgentJson(analyzerText)
  const parsedTask = tryParseAgentJson(taskCreatorText)

  // Priority: server pre-parsed → client-parsed → null
  const reportMd  = preParsedReportMd  ?? (typeof parsedAnalyzer?.report_md === 'string' ? parsedAnalyzer.report_md : null)
  const findings  = preParsedFindings  ?? (Array.isArray(parsedAnalyzer?.findings) ? parsedAnalyzer.findings : null)
  const followUps = preParsedFollowUps ?? (Array.isArray(parsedAnalyzer?.follow_up_items) ? parsedAnalyzer.follow_up_items : null)
  const subject   = preParsedSubject   ?? (typeof parsedTask?.subject === 'string' ? parsedTask.subject : null)
  const htmlBody  = preParsedHtmlBody  ?? (typeof parsedTask?.html_body === 'string' ? parsedTask.html_body : null)

  const runInfo = parseProviderNote(providerNote)

  // Priority counts — derived from followUps (most actionable source)
  const priorityCounts = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, FYI: 0 }
  for (const it of (followUps || [])) {
    const { level } = parsePriority(it?.title ?? '')
    if (level && priorityCounts[level] != null) priorityCounts[level]++
    else priorityCounts.FYI++
  }
  const hasPrioritySummary = showAnalyzer && (followUps?.length ?? 0) > 0

  // iframe auto-height
  const handleIframeLoad = () => {
    try {
      const iframe = iframeRef.current
      if (iframe?.contentDocument?.body) {
        const h = iframe.contentDocument.body.scrollHeight
        if (h > 60) iframe.style.height = `${h + 40}px`
      }
    } catch { /* cross-origin guard */ }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ------------------------------------------------------------------ */}
      {/* RUN INFO BAR                                                        */}
      {/* ------------------------------------------------------------------ */}
      {runInfo && (
        <div style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
          padding: '8px 14px', borderRadius: 8,
          background: runInfo.isStagePreview ? '#eff6ff' : runInfo.isDryRun ? '#fffbeb' : '#f0fdf4',
          border: `1px solid ${runInfo.isStagePreview ? '#bfdbfe' : runInfo.isDryRun ? '#fde68a' : '#bbf7d0'}`,
        }}>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
            background: runInfo.isStagePreview ? '#dbeafe' : runInfo.isDryRun ? '#fef3c7' : '#dcfce7',
            color: runInfo.isStagePreview ? '#1d4ed8' : runInfo.isDryRun ? '#92400e' : '#15803d',
          }}>
            {runInfo.isStagePreview
              ? 'Stage preview'
              : runInfo.isDryRun
                ? 'Dry Run'
                : runInfo.isLive
                  ? 'Live Run'
                  : 'Run'}
          </span>
          {runInfo.model && (
            <span style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>
              {runInfo.model}
            </span>
          )}
          {runInfo.links != null && (
            <span style={{ fontSize: 11, color: '#64748b' }}>
              · {runInfo.links} link{runInfo.links !== 1 ? 's' : ''} processed
            </span>
          )}
          {runInfo.emailNotSent && (
            <span style={{ fontSize: 11, color: '#b45309', marginLeft: 4 }}>
              · email not sent
            </span>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* PRIORITY SUMMARY BAR                                               */}
      {/* ------------------------------------------------------------------ */}
      {showTask && analyzerInputExcerpt ? (
        <details style={{ marginBottom: 4 }}>
          <summary style={{ fontSize: 12, color: '#64748b', cursor: 'pointer', userSelect: 'none' }}>
            Analyzer input (excerpt)
          </summary>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              fontSize: 11,
              margin: '8px 0 0',
              padding: 12,
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              maxHeight: 160,
              overflow: 'auto',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              color: '#475569',
            }}
          >
            {analyzerInputExcerpt}
          </pre>
        </details>
      ) : null}

      {hasPrioritySummary && (
        <div style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
          padding: '10px 16px', borderRadius: 8,
          background: '#fff', border: '1px solid #e2e8f0',
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 4 }}>
            Priority
          </span>
          {priorityCounts.CRITICAL > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 20, background: '#fee2e2', color: '#b91c1c', fontSize: 12, fontWeight: 700 }}>
              🔴 {priorityCounts.CRITICAL} Critical
            </span>
          )}
          {priorityCounts.HIGH > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 20, background: '#ffedd5', color: '#c2410c', fontSize: 12, fontWeight: 700 }}>
              🟠 {priorityCounts.HIGH} High
            </span>
          )}
          {priorityCounts.MEDIUM > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 20, background: '#fef9c3', color: '#854d0e', fontSize: 12, fontWeight: 700 }}>
              🟡 {priorityCounts.MEDIUM} Medium
            </span>
          )}
          {priorityCounts.FYI > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 20, background: '#f1f5f9', color: '#475569', fontSize: 12, fontWeight: 600 }}>
              {priorityCounts.FYI} FYI
            </span>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* EXECUTIVE BRIEFING                                                  */}
      {/* ------------------------------------------------------------------ */}
      {showAnalyzer && (reportMd || (typeof analyzerText === 'string' && analyzerText.trim())) && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
          <SectionHeader
            label="Executive Briefing"
            action={reportMd ? (
              <button
                type="button"
                onClick={() => setBriefingExpanded((v) => !v)}
                style={{
                  fontSize: 11, fontWeight: 600, color: '#2563eb', background: 'none',
                  border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: 4,
                }}
              >
                {briefingExpanded ? 'Collapse ▲' : 'Expand ▼'}
              </button>
            ) : null}
          />
          <div style={{ position: 'relative' }}>
            <div style={{
              padding: '16px 20px',
              maxHeight: briefingExpanded ? 'none' : 280,
              overflow: briefingExpanded ? 'visible' : 'hidden',
            }}>
              {reportMd ? (
                <div className="bc-report-md" style={{ fontSize: 14, lineHeight: 1.65, color: '#1e293b' }}>
                  <style>{`
                    .bc-report-md h1 { font-size: 17px; font-weight: 700; margin: 16px 0 6px; color: #0f172a; }
                    .bc-report-md h2 { font-size: 15px; font-weight: 700; margin: 14px 0 5px; color: #1e293b; border: none; padding: 0; }
                    .bc-report-md h3 { font-size: 13px; font-weight: 700; margin: 10px 0 3px; color: #334155; }
                    .bc-report-md p  { margin: 6px 0; }
                    .bc-report-md ul, .bc-report-md ol { padding-left: 20px; margin: 6px 0; }
                    .bc-report-md li { margin-bottom: 4px; }
                    .bc-report-md strong { color: #0f172a; }
                    .bc-report-md hr { border: none; border-top: 1px solid #e2e8f0; margin: 14px 0; }
                    .bc-report-md code { font-size: 12px; background: #f1f5f9; padding: 1px 5px; border-radius: 4px; }
                    .bc-report-md blockquote { border-left: 3px solid #e2e8f0; margin: 8px 0; padding: 4px 12px; color: #475569; }
                  `}</style>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                    {reportMd}
                  </ReactMarkdown>
                </div>
              ) : (() => {
                // Fallback: raw text couldn't be parsed as structured JSON
                const raw = typeof analyzerText === 'string' ? analyzerText.trim() : ''
                const looksLikeJson = raw.startsWith('{') || raw.startsWith('```')
                if (!looksLikeJson) {
                  // Probably plain markdown — render it
                  return (
                    <div className="bc-report-md" style={{ fontSize: 14, lineHeight: 1.65, color: '#1e293b' }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{raw}</ReactMarkdown>
                    </div>
                  )
                }
                return (
                  <>
                    <div style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6,
                      padding: '10px 14px', marginBottom: 10, fontSize: 12, color: '#92400e',
                    }}>
                      <span style={{ fontSize: 14, flexShrink: 0 }}>⚠️</span>
                      <span>
                        Could not parse structured output from the model.
                        The raw response is shown below. Check the <strong>Raw JSON</strong> section at the bottom for the full text.
                      </span>
                    </div>
                    <pre style={{
                      whiteSpace: 'pre-wrap', fontSize: 12, margin: 0, color: '#475569',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      maxHeight: 200, overflow: 'auto',
                      background: '#f8fafc', borderRadius: 6, padding: '10px 12px',
                    }}>
                      {raw.slice(0, 800)}{raw.length > 800 ? '\n… (truncated — see Raw JSON below)' : ''}
                    </pre>
                  </>
                )
              })()}
            </div>
            {/* Gradient fade when collapsed */}
            {reportMd && !briefingExpanded && (
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: 64,
                background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))',
                display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                paddingBottom: 8,
              }}>
                <button
                  type="button"
                  onClick={() => setBriefingExpanded(true)}
                  style={{
                    fontSize: 12, fontWeight: 600, color: '#2563eb',
                    background: '#fff', border: '1px solid #e2e8f0',
                    borderRadius: 6, padding: '4px 14px', cursor: 'pointer',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  }}
                >
                  Show full briefing ▼
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* ACTION ITEMS                                                        */}
      {/* ------------------------------------------------------------------ */}
      {showAnalyzer && followUps?.length ? (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
          <SectionHeader
            label="Action Items"
            count={followUps.length}
            right={priorityCounts.CRITICAL > 0 ? `${priorityCounts.CRITICAL} critical` : null}
          />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {followUps.map((it, i) => {
              if (!it) return null
              const titleRaw = typeof it.title === 'string' ? it.title : null
              const rationale = typeof it.rationale === 'string' ? it.rationale : null
              if (!titleRaw && !rationale) return null
              const { level, text: titleText } = parsePriority(titleRaw ?? '')
              const cfg = level ? PRIORITY_CONFIG[level] : null
              return (
                <div
                  key={i}
                  style={{
                    borderTop: i === 0 ? 'none' : '1px solid #f1f5f9',
                    borderLeft: `4px solid ${cfg ? cfg.border : '#e2e8f0'}`,
                    background: cfg ? cfg.bg : '#fff',
                    padding: '12px 16px',
                    display: 'flex',
                    gap: 12,
                  }}
                >
                  {/* Number badge */}
                  <div style={{
                    flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
                    background: cfg ? cfg.chipBg : '#f1f5f9',
                    color: cfg ? cfg.color : '#94a3b8',
                    fontSize: 11, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginTop: 1,
                  }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: rationale ? 6 : 0, flexWrap: 'wrap' }}>
                      {cfg && <PriorityChip level={level} />}
                      <span style={{ fontSize: 13, fontWeight: 600, color: cfg ? cfg.color : '#0f172a', lineHeight: 1.45 }}>
                        {titleText}
                      </span>
                    </div>
                    {rationale && (
                      <p style={{ margin: 0, fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
                        {rationale}
                      </p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* EMAIL DRAFT                                                         */}
      {/* ------------------------------------------------------------------ */}
      {showTask && (htmlBody || (typeof taskCreatorText === 'string' && taskCreatorText.trim())) && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
          <SectionHeader label="Email Draft" />
          {subject && (
            <div style={{
              padding: '10px 16px', borderBottom: '1px solid #e2e8f0',
              background: '#fff', display: 'flex', alignItems: 'flex-start', gap: 8,
            }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0, marginTop: 3 }}>
                Subject
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', lineHeight: 1.45 }}>
                {subject}
              </span>
            </div>
          )}
          {htmlBody ? (
            <iframe
              ref={iframeRef}
              title="Email HTML preview"
              sandbox="allow-same-origin"
              srcDoc={wrapEmailSrcDoc(htmlBody)}
              onLoad={handleIframeLoad}
              style={{ width: '100%', minHeight: 360, height: 360, border: 'none', background: '#fff', display: 'block' }}
            />
          ) : (
            <pre style={{ margin: 0, padding: '12px 16px', whiteSpace: 'pre-wrap', fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', color: '#475569', background: '#f8fafc' }}>
              {taskCreatorText}
            </pre>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* REFERENCE: FINDINGS (collapsed)                                     */}
      {/* ------------------------------------------------------------------ */}
      {showAnalyzer && findings?.length ? (
        <details>
          <summary style={{ fontSize: 12, color: '#64748b', cursor: 'pointer', userSelect: 'none', padding: '2px 0' }}>
            Reference: Findings ({findings.length} items)
          </summary>
          <div style={{ marginTop: 8, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 3 }}>
              {findings.map((f, i) => {
                const raw = typeof f === 'string' ? f : JSON.stringify(f)
                const { level, text } = parsePriority(raw)
                const cfg = level ? PRIORITY_CONFIG[level] : null
                return (
                  <div
                    key={i}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '6px 10px', borderRadius: 6,
                      background: cfg ? cfg.bg : 'transparent',
                      borderLeft: `3px solid ${cfg ? cfg.border : 'transparent'}`,
                    }}
                  >
                    {cfg ? <PriorityChip level={level} /> : (
                      <span style={{ color: '#cbd5e1', fontSize: 14, lineHeight: 1.1, marginTop: 1, flexShrink: 0 }}>•</span>
                    )}
                    <span style={{ fontSize: 12, lineHeight: 1.55, color: cfg ? '#0f172a' : '#64748b', fontWeight: cfg ? 500 : 400 }}>
                      {text}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        </details>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* RAW JSON (collapsed)                                                */}
      {/* ------------------------------------------------------------------ */}
      <details>
        <summary style={{ fontSize: 11, color: '#94a3b8', cursor: 'pointer', userSelect: 'none' }}>
          Raw JSON
        </summary>
        <pre style={{
          whiteSpace: 'pre-wrap', fontSize: 11, margin: '8px 0 0', padding: 12,
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
          maxHeight: 240, overflow: 'auto',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', color: '#475569',
        }}>
          {JSON.stringify(
            {
              ...(showAnalyzer ? { analyzer: parsedAnalyzer ?? analyzerText } : {}),
              ...(showTask ? { task: parsedTask ?? taskCreatorText } : {}),
            },
            null,
            2
          )}
        </pre>
      </details>

    </div>
  )
}
